import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import course03 from "../../../course/course-03/course";
import lesson0114 from "../lesson-14/lesson";
import lesson0118 from "../lesson-18/lesson";
import lesson0308 from "../../course-03/lesson-08/lesson";

const linkLesson0114 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0114.id);
const linkLesson0118 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0118.id);
const linkLesson0308 = getLinkForCourseLesson(course03.id, lesson0308.id);

const theory = ` In this Lesson we consider a method to calculate the acoustic field in a cavity which is largely used in room acoustics (see <a href="${linkLesson0308}" target="_blank">Lesson 3.8</a>). We first reconsider the problem addressed in <a href="${linkLesson0118}" target="_blank">Lesson 1.18</a> but considering a single point source i.e. a monopole (see <a href="${linkLesson0114}" target="_blank">Lesson 1.14</a>). The general solution results for a distribution of sources which can be decomposed as a sum of point sources. So the proposed approach is equivalent to the so-called modal approach discussed in <a href="${linkLesson0118}" target="_blank">Lesson 1.18</a>.
<h3 class=sectionHead>Infinite field</h3>
<p>
   Considering a monopole delivering a volume velocity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
   </math>
   at position
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
   </math>, the pressure field in the frequency domain is given by
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>ω</mi>
                  <msup>
                     <mrow>
                        <mi>Q</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jk</mi>
                        <mo class=MathClass-rel>|</mo>
                        <mi>r</mi>
                        <mo class=MathClass-bin>−</mo>
                        <msup>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mi>′</mi>
                           </mrow>
                        </msup>
                        <mo class=MathClass-rel>|</mo>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
                  <mi>r</mi>
                  <mo class=MathClass-bin>−</mo>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>r</mi>
   </math>
   is the location of the observation point.
<h3 class=sectionHead>Field limited by a reflecting wall</h3>
<p>
   In a field limited by an infinite plane perfectly reflecting, the boundary condition has to be satisfied (see <a href="#" data-img-src=${config.ASSETS_URL}/01/24/theory/figure-1.svg data-img-alt="Figure 1. Source and its image.">Figure 1</a>). Considering for example a perfectly reflecting wall at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>, the condition
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>∂p</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂n</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   leads to
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>ω</mi>
                  <msup>
                     <mrow>
                        <mi>Q</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <mrow>
               <mo fence=true form=prefix>[</mo>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mi>e</mi>
                           </mrow>
                           <mrow>
                              <mo class=MathClass-bin>−</mo>
                              <mi mathvariant=italic>jk</mi>
                              <mo class=MathClass-rel>|</mo>
                              <mi>r</mi>
                              <mo class=MathClass-bin>−</mo>
                              <msup>
                                 <mrow>
                                    <mi>r</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>′</mi>
                                 </mrow>
                              </msup>
                              <mo class=MathClass-rel>|</mo>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-rel>|</mo>
                        <mi>r</mi>
                        <mo class=MathClass-bin>−</mo>
                        <msup>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mi>′</mi>
                           </mrow>
                        </msup>
                        <mo class=MathClass-rel>|</mo>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mi>e</mi>
                           </mrow>
                           <mrow>
                              <mo class=MathClass-bin>−</mo>
                              <mi mathvariant=italic>jk</mi>
                              <mo class=MathClass-rel>|</mo>
                              <mi>r</mi>
                              <mo class=MathClass-bin>−</mo>
                              <msup>
                                 <mrow>
                                    <mi>r</mi>
                                 </mrow>
                                 <mrow>
                                    <mi class=MathClass-op>″</mi>
                                    <mo>⁡</mo>
                                 </mrow>
                              </msup>
                              <mo class=MathClass-rel>|</mo>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-rel>|</mo>
                        <mi>r</mi>
                        <mo class=MathClass-bin>−</mo>
                        <msup>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mi class=MathClass-op>″</mi>
                              <mo>⁡</mo>
                           </mrow>
                        </msup>
                        <mo class=MathClass-rel>|</mo>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo fence=true form=postfix>]</mo>
            </mrow>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>y</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>z</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi class=MathClass-op>″</mi>
            <mo>⁡</mo>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-open>(</mo>
      <mo class=MathClass-bin>−</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>y</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>z</mi>
      <mo class=MathClass-close>)</mo>
   </math>. So, solution appears to be the sum of the field generated by a monopole delivering a volume velocity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Q</mi>
   </math>
   at position
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
   </math>
   and another monopole delivering a volume velocity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Q</mi>
   </math>
   at position
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi class=MathClass-op>″</mi>
            <mo>⁡</mo>
         </mrow>
      </msup>
   </math>. The second monopole is outside the considered field and is called image source by analogy with optics: a perfectly reflected wall is an acoustic mirror. The reflecting wall is not necessarily perfectly reflecting. It can reflect waves with a given reflection coefficient
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>R</mi>
   </math>. In that situation the image source delivers a volume velocity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi class=MathClass-op>″</mi>
            <mo>⁡</mo>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mi>R</mi>
      <msup>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
   </math>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/01/24/theory/figure-1.svg alt="Figure 1. Source and its image."><figcaption class=caption>Figure 1. Source and its image.</figcaption></figure>
<h3 class=sectionHead>Field with two parallel reflecting planes</h3>
<p>
   In a field limited by two infinite planes perfectly reflecting, the boundary conditions have to be satisfied (see <a href="#" data-img-src=${config.ASSETS_URL}/01/24/theory/figure-2.svg data-img-alt="Figure 2. Source between two walls and its images.">Figure 2</a>). Considering for example a perfectly reflecting wall at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   and another at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>L</mi>
   </math>, the conditions
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>∂p</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂n</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>∂p</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂n</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
         </mrow>
         <mrow>
            <mi>L</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   imposes that the distribution of sources might be periodic which is equivalent to the condition
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mi mathvariant=italic>kL</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>. This can be interpreted by the fact that the reflection of the source on the first wall leads to an image source which reflect on the second wall, leading to a second image source which reflects on the first wall and so on. The distribution of sources can be seen as the repetition two series of sources with source at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mo class=MathClass-bin>+</mo>
      <mn>2</mn>
      <mi mathvariant=italic>nL</mi>
   </math>
   and another at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-bin>−</mo>
      <msup>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mo class=MathClass-bin>+</mo>
      <mn>2</mn>
      <mi mathvariant=italic>nL</mi>
   </math>
   with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>n</mi>
      <mo class=MathClass-rel>∈</mo>
      <mi>Z</mi>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>0</mn>
      <mo class=MathClass-rel><</mo>
      <msup>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mo class=MathClass-rel><</mo>
      <mi>L</mi>
   </math>. The pressure field is then given by
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>ω</mi>
                  <msup>
                     <mrow>
                        <mi>Q</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <munderover accentunder=false accent=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mo class=MathClass-bin>−</mo>
                  <mi>∞</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mi>∞</mi>
               </mrow>
            </munderover>
            <mrow>
               <mo fence=true form=prefix>[</mo>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mi>e</mi>
                           </mrow>
                           <mrow>
                              <mo class=MathClass-bin>−</mo>
                              <mi mathvariant=italic>jk</mi>
                              <mo class=MathClass-rel>|</mo>
                              <mi>r</mi>
                              <mo class=MathClass-bin>−</mo>
                              <msubsup>
                                 <mrow>
                                    <mi>r</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>n</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>′</mi>
                                 </mrow>
                              </msubsup>
                              <mo class=MathClass-rel>|</mo>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-rel>|</mo>
                        <mi>r</mi>
                        <mo class=MathClass-bin>−</mo>
                        <msubsup>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mi>n</mi>
                           </mrow>
                           <mrow>
                              <mi>′</mi>
                           </mrow>
                        </msubsup>
                        <mo class=MathClass-rel>|</mo>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mi>e</mi>
                           </mrow>
                           <mrow>
                              <mo class=MathClass-bin>−</mo>
                              <mi mathvariant=italic>jk</mi>
                              <mo class=MathClass-rel>|</mo>
                              <mi>r</mi>
                              <mo class=MathClass-bin>−</mo>
                              <msubsup>
                                 <mrow>
                                    <mi>r</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>n</mi>
                                 </mrow>
                                 <mrow>
                                    <mo>″</mo>
                                 </mrow>
                              </msubsup>
                              <mo class=MathClass-rel>|</mo>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-rel>|</mo>
                        <mi>r</mi>
                        <mo class=MathClass-bin>−</mo>
                        <msubsup>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mi>n</mi>
                           </mrow>
                           <mrow>
                              <mo>″</mo>
                           </mrow>
                        </msubsup>
                        <mo class=MathClass-rel>|</mo>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo fence=true form=postfix>]</mo>
            </mrow>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>nL</mi>
      <mo class=MathClass-bin>+</mo>
      <msup>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mo class=MathClass-punc>,</mo>
      <msup>
         <mrow>
            <mi>y</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mo class=MathClass-punc>,</mo>
      <msup>
         <mrow>
            <mi>z</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mo class=MathClass-close>)</mo>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi class=MathClass-op>″</mi>
            <mo>⁡</mo>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>nL</mi>
      <mo class=MathClass-bin>−</mo>
      <msup>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mo class=MathClass-punc>,</mo>
      <msup>
         <mrow>
            <mi>y</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mo class=MathClass-punc>,</mo>
      <msup>
         <mrow>
            <mi>z</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
      <mo class=MathClass-close>)</mo>
   </math>. When the walls are not perfectly reflecting, reflection coefficients
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>L</mi>
         </mrow>
      </msub>
   </math>
   have to be considered. This leads to
<table class=equation>
   <tr>
      <td style="font-size: 9px">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi>ω</mi>
                  <msup>
                     <mrow>
                        <mi>Q</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <munderover accentunder=false accent=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mo class=MathClass-bin>−</mo>
                  <mi>∞</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mi>∞</mi>
               </mrow>
            </munderover>
            <mrow>
               <mo fence=true form=prefix>[</mo>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>Q</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msubsup>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mi>e</mi>
                           </mrow>
                           <mrow>
                              <mo class=MathClass-bin>−</mo>
                              <mi mathvariant=italic>jk</mi>
                              <mo class=MathClass-rel>|</mo>
                              <mi>r</mi>
                              <mo class=MathClass-bin>−</mo>
                              <msubsup>
                                 <mrow>
                                    <mi>r</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>n</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>′</mi>
                                 </mrow>
                              </msubsup>
                              <mo class=MathClass-rel>|</mo>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-rel>|</mo>
                        <mi>r</mi>
                        <mo class=MathClass-bin>−</mo>
                        <msubsup>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mi>n</mi>
                           </mrow>
                           <mrow>
                              <mi>′</mi>
                           </mrow>
                        </msubsup>
                        <mo class=MathClass-rel>|</mo>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <msubsup>
                     <mrow>
                        <mi>Q</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                     <mrow>
                        <mo>″</mo>
                     </mrow>
                  </msubsup>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mi>e</mi>
                           </mrow>
                           <mrow>
                              <mo class=MathClass-bin>−</mo>
                              <mi mathvariant=italic>jk</mi>
                              <mo class=MathClass-rel>|</mo>
                              <mi>r</mi>
                              <mo class=MathClass-bin>−</mo>
                              <msubsup>
                                 <mrow>
                                    <mi>r</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>n</mi>
                                 </mrow>
                                 <mrow>
                                    <mo>″</mo>
                                 </mrow>
                              </msubsup>
                              <mo class=MathClass-rel>|</mo>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-rel>|</mo>
                        <mi>r</mi>
                        <mo class=MathClass-bin>−</mo>
                        <msubsup>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mi>n</mi>
                           </mrow>
                           <mrow>
                              <mo>″</mo>
                           </mrow>
                        </msubsup>
                        <mo class=MathClass-rel>|</mo>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo fence=true form=postfix>]</mo>
            </mrow>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msubsup>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msubsup>
      <mo class=MathClass-rel>=</mo>
      <msubsup>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-rel>|</mo>
            <mi>n</mi>
            <mo class=MathClass-rel>|</mo>
         </mrow>
      </msubsup>
      <msubsup>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-rel>|</mo>
            <mi>n</mi>
            <mo class=MathClass-rel>|</mo>
         </mrow>
      </msubsup>
      <msup>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msubsup>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
         <mrow>
            <mi class=MathClass-op>″</mi>
            <mo>⁡</mo>
         </mrow>
      </msubsup>
      <mo class=MathClass-rel>=</mo>
      <msubsup>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-rel>|</mo>
            <mi>n</mi>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
            <mo class=MathClass-rel>|</mo>
         </mrow>
      </msubsup>
      <msubsup>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-rel>|</mo>
            <mi>n</mi>
            <mo class=MathClass-rel>|</mo>
         </mrow>
      </msubsup>
      <msup>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
   </math>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/01/24/theory/figure-2.svg alt="Figure 2. Source between two walls and its images."><figcaption class=caption>Figure 2. Source between two walls and its images.</figcaption></figure>
<h3 class=sectionHead>Two planes with an angle</h3>
<p>When a source is placed in the front of an angle, two situations have to be distinguished.
<h4 class=subsectionHead>Obtuse angle</h4>
<p>
   When the angle is obtuse (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ϕ</mi>
      <mo class=MathClass-rel>></mo>
      <mi>π</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>2</mn>
   </math>) the source reflects on both walls leading to two images with only one reflection plus an image with two reflections. The number of images is finite and here equal to 3 (see <a href="#" data-img-src=${config.ASSETS_URL}/01/24/theory/figure-3.svg data-img-alt="Figure 3. Source in front of an obtuse angle and its images.">Figure 3</a>).
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/01/24/theory/figure-3.svg alt="Figure 3. Source in front of an obtuse angle and its images."><figcaption class=caption>Figure 3. Source in front of an obtuse angle and its images.</figcaption></figure>
<h4 class=subsectionHead>Sharp angle</h4>
<p>
   When the angle is sharp (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ϕ</mi>
      <mo class=MathClass-rel><</mo>
      <mi>π</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>2</mn>
   </math>) the source reflects on both walls leading to two images with only one reflection plus a finite number of images with two or more reflections. The number of images is finite and the smaller the angle the larger the number of image sources (see <a href="#" data-img-src=${config.ASSETS_URL}/01/24/theory/figure-4.svg data-img-alt="Figure 4. Source in front of a sharp angle and its images.">Figure 4</a>).
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/01/24/theory/figure-4.svg alt="Figure 4. Source in front of a sharp angle and its images."><figcaption class=caption>Figure 4. Source in front of a sharp angle and its images.</figcaption></figure>
<h3 class=sectionHead>Cavity</h3>
<p>
   In the case of a parallelepiped cavity the distribution of sources is periodic in the 3 directions. A cell with 8 sources which dimension is 8 times that of the cavity is repeated. The source distribution is the triple product of infinite sum of sources. The problem becomes more intricate in the case of non-parallelepiped cavity. In some cases a periodic structure can be found but in most situations, the distribution of sources looks rather random. So the sum of the sources might be truncated. Different strategies can be made in order to limit the number of sources. One is to consider a maximum number of reflections. The main advantage of this strategy is its simplicity but it do not ensure that the more contributive sources are taken into account. Another possibility is to consider the image sources which are the closest from the initial source (<a href="#" data-img-src=${config.ASSETS_URL}/01/24/theory/figure-5.svg data-img-alt="Figure 5. Example of image source cloud truncation. —–, cavity boundaries; - - - -, characteristic and truncation circles of radii <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>r</mi></mrow><mrow><mi>c</mi></mrow></msub></math> and <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>r</mi></mrow><mrow><mi>t</mi></mrow></msub></math> centered on the receiver; <strong><span class=cmbx-10>+</span></strong>, sources taken into account; +, ignored image sources.">Figure 5</a>).
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/01/24/theory/figure-5.svg alt="Figure 5. Example of image source cloud truncation. —–, cavity boundaries; - - - -, characteristic and truncation circles of radii <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>r</mi></mrow><mrow><mi>c</mi></mrow></msub></math> and <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>r</mi></mrow><mrow><mi>t</mi></mrow></msub></math> centered on the receiver; <strong><span class=cmbx-10>+</span></strong>, sources taken into account; +, ignored image sources."><figcaption class=caption>Figure 5. Example of image source cloud truncation. —–, cavity boundaries; - - - -, characteristic and truncation circles of radii <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>r</mi></mrow><mrow><mi>c</mi></mrow></msub></math> and <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>r</mi></mrow><mrow><mi>t</mi></mrow></msub></math> centered on the receiver; <strong><span class=cmbx-10>+</span></strong>, sources taken into account; +, ignored image sources.</figcaption></figure>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
  <dt>[1]
  <dd>L.J. Beranek. Acoustics. The American Institute of Physics, New York, 1986.
  <dt>[2]
  <dd>J. Cuenca. Wave models for the flexural vibrations of thin plates - Model of the vibrations of polygonal plates by the image source method. PhD thesis, Université du Maine, Le Mans, France, October 2009.
  <dt>[3]
  <dd>A.D. Pierce. Acoustics: an Introduction to its Physical Principles and Applications. MCGraw-Hill Book Company, Inc., New York, 1981.
  <dt>[4]
  <dd>S. Temkin. Elements of Acoustics. Wiley, New York, 1981.
</dl>`;

export default theory;
